import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SendIcon from "@mui/icons-material/Send";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { DigipingMark, DIMENSIONS, getDimensionMeta } from "../../digiping";
import { getSocket, initializeSocket } from "../../socket";
import {
  fetchChatHistory,
  fetchChatPeers,
  sendChatMessage,
} from "../funtionsBD/digipingChatService";
import { readStoredUser } from "../../utils/homeRole";
import { canBrowseClubChatRoster } from "../../utils/digipingOpenContext";
import {
  fetchSmartProfile,
  fetchSmartTrail,
  reviewSmartEvent,
} from "../funtionsBD/digipingSmartService";
import {
  acknowledgeFlttRankingNotice,
  getPlayerProfileStats,
} from "../funtionsBD/Mainservice";
import { CoachObjectiveMetricPicker } from "../CoachObjectiveMetricPicker";
import { SprintExerciseTable } from "../SprintExerciseTable";
import { FlttRankingSlip } from "../FlttRankingSlip";
import { canShowFlttRankingChart } from "../../utils/flttRankingDisplay";
import { openDigitalTwin } from "../HomeDailyFocus";
import { resolveSignupSeasonResult } from "../../utils/playerSeasonResult";
import { localizedDraftName } from "../../utils/localizedCatalogFields";
import { useLiveVoiceTranscript } from "../../hooks/useLiveVoiceTranscript";
import { checkBrowserCapability } from "../../voice";
import CoachVoiceButton from "./CoachVoiceButton";
import "../AICoach/DigipingChat.css";
import "../ProfileSmartTab.css";
import "./DigipingPeopleChat.css";

function ConnectedBadge({ connected, t }) {
  if (!connected) return null;
  return (
    <span className="dp-people__connected" title={t("digipingChatConnectedHint")}>
      {t("digipingChatConnected")}
    </span>
  );
}

function readActiveClubId(stored) {
  const u = stored || {};
  return (
    u.club_id ||
    u.club_details?.club_id ||
    u.club?.club_id ||
    undefined
  );
}

function peerLabel(p) {
  return p?.name || p?.user_alias || p?.user_first_name || "Player";
}

function messageSenderId(m) {
  return m?.sender_id || m?.sender?.user_id || null;
}

function slotDetailLines(profile, dim, t) {
  const slots = profile?.slots || {};
  const s = slots[dim] || {};
  const labels = s.labels || {};
  const goals = Array.isArray(profile?.goals) ? profile.goals : [];
  if (dim === "S") {
    const primary =
      labels.primaryFocus ||
      goals.find((g) => g.is_primary)?.label ||
      goals[0]?.label;
    const secondary =
      labels.secondaryFocus ||
      goals
        .filter((g) => !g.is_primary)
        .map((g) => g.label)
        .filter(Boolean)
        .join(", ");
    return [
      primary && { label: t("digipingFocusStar"), value: primary },
      secondary && { label: t("digipingSecondaryDot"), value: secondary },
      labels.style && { label: t("digipingStyle"), value: labels.style },
      labels.competitionYears && {
        label: t("digipingCompetition"),
        value: labels.competitionYears,
      },
      (labels.classement || s.classement) && {
        label: t("digipingRanking"),
        value: labels.classement || s.classement,
      },
      (labels.rankingPoints ||
        (s.rankingPoints != null ? `${s.rankingPoints} pts` : null)) && {
        label: t("digipingPoints"),
        value:
          labels.rankingPoints ||
          (s.rankingPoints != null ? `${s.rankingPoints} pts` : null),
      },
    ].filter(Boolean);
  }
  if (dim === "M") {
    const prog = profile?.pathMeta?.objectiveProgress;
    return [
      labels.level && { label: t("digipingLevel"), value: labels.level },
      s.weeklyTraining?.sessionsPerWeek
        ? {
            label: t("digipingVolume"),
            value: `${s.weeklyTraining.sessionsPerWeek}×/${t("digipingPerWeek")}`,
          }
        : null,
      prog?.score != null
        ? { label: t("digipingProgress"), value: `${prog.score}%` }
        : null,
    ].filter(Boolean);
  }
  if (dim === "A") {
    const ms = (s.milestones || [])
      .map((m) => m.label || m)
      .filter(Boolean);
    if (ms.length) return ms.map((value) => ({ value }));
    return [s.grip, s.hand].filter(Boolean).map((value) => ({ value }));
  }
  if (dim === "R") {
    const agree = profile?.coachAgreement?.status;
    return [
      s.whyItMatters && { value: s.whyItMatters },
      agree ? { label: t("digipingAgreement"), value: agree } : null,
    ].filter(Boolean);
  }
  if (dim === "T") {
    const cp = profile?.pathMeta?.cyclePresence || profile?.cyclePresence;
    return [
      s.cycleStart
        ? {
            label: t("digipingCycle"),
            value: `${s.cycleWeeks || 4}${t("digipingWeeksShort")} · ${String(s.cycleStart).slice(0, 10)}`,
          }
        : null,
      cp?.present_sessions != null
        ? {
            label: t("digipingPresence"),
            value: `${cp.present_sessions}/${cp.expected_to_date}`,
          }
        : null,
    ].filter(Boolean);
  }
  return [];
}

function resolveExIdForMetricPicker(ev) {
  const p = ev?.payload || {};
  return (
    p.selected_ex_id ||
    p.parent_ex_id ||
    p.resolved_ex_id ||
    p.base_ex_id ||
    null
  );
}

function coachDimLabel(status, t) {
  if (status === "complete") return t("smartTargetApproved", { defaultValue: "Validated" });
  if (status === "partial") return t("smartTargetPendingCoach", { defaultValue: "Pending" });
  return t("smartDimensionEmpty", { defaultValue: "—" });
}

function CoachSmartGrid({ status = {}, labels = {}, activeDim, pendingDims = [], onSelectDimension, t }) {
  return (
    <div className="dp-smart-grid" role="tablist" aria-label={t("smartTab")}>
      {DIMENSIONS.map((dim) => {
        const st = status[dim] || "empty";
        const isActive = activeDim === dim;
        const isPending = pendingDims.includes(dim);
        return (
          <button
            key={dim}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-label={`${dim} · ${labels[dim]?.label || dim}`}
            className={`dp-smart-grid__cell is-${st}${isActive ? " is-active" : ""}${isPending ? " is-pending" : ""}`}
            onClick={() => onSelectDimension?.(dim)}
          >
            <span className="dp-smart-grid__letter">{dim}</span>
            {isPending ? <span className="dp-smart-grid__dot" /> : null}
          </button>
        );
      })}
    </div>
  );
}

function PlayerSmartTrail({
  peer,
  lang,
  onReviewed,
  activeDim,
  onSelectDimension,
}) {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [trailData, setTrailData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notifyingRank, setNotifyingRank] = useState(false);
  const [metricKindsByEvent, setMetricKindsByEvent] = useState({});
  const labels = useMemo(() => getDimensionMeta(lang), [lang]);

  const load = useCallback(async () => {
    if (!peer?.user_id) return;
    try {
      const [p, trail] = await Promise.all([
        fetchSmartProfile(peer.user_id, lang),
        fetchSmartTrail(peer.user_id, lang).catch(() => null),
      ]);
      setProfile(p);
      let nextTrail = trail;
      // Trail historically omitted rankingHistory; fill from profile stats when needed.
      if (
        nextTrail &&
        !canShowFlttRankingChart(nextTrail.rankingHistory) &&
        peer.user_id
      ) {
        try {
          const statsRes = await getPlayerProfileStats({
            view_user_id: peer.user_id,
          });
          const stats = statsRes?.data?.data;
          if (stats?.rankingHistory?.length || stats?.officialRanking) {
            nextTrail = {
              ...nextTrail,
              officialRanking:
                nextTrail.officialRanking || stats.officialRanking || null,
              rankingHistory:
                stats.rankingHistory?.length
                  ? stats.rankingHistory
                  : nextTrail.rankingHistory || [],
              rankingNoticePending:
                nextTrail.rankingNoticePending ??
                Boolean(stats.officialRanking?.rankingNoticePending),
            };
          }
        } catch (statsErr) {
          console.warn(
            "PlayerSmartTrail ranking stats:",
            statsErr?.message || statsErr
          );
        }
      }
      setTrailData(nextTrail);
    } catch (err) {
      console.warn("PlayerSmartTrail:", err?.message || err);
    }
  }, [peer?.user_id, lang]);

  useEffect(() => {
    load();
  }, [load]);

  const status = profile?.status || {};
  const letters = peer?.letters?.length
    ? Object.fromEntries(peer.letters.map((L) => [L.letter, L.status]))
    : status;
  const railStatus = { ...letters, ...status };
  const ex =
    profile?.personalizedExercise ||
    profile?.slots?.S?.personalizedExercise ||
    peer?.personalized_exercise ||
    null;
  const goalExercises =
    profile?.goalExercises ||
    profile?.slots?.S?.goalExercises ||
    peer?.goal_exercises ||
    [];
  const pendingEvents = peer?.pending_events || [];
  const rankingNoticePending = Boolean(trailData?.rankingNoticePending);
  const pendingDims = [
    ...new Set(
      [
        ...(profile?.coachPendingDims || []),
        ...pendingEvents.map((e) => String(e.dimension || "").toUpperCase()),
        ex?.status === "pending_coach" || ex?.status === "proposed" ? "S" : null,
        ...goalExercises
          .filter((g) => g?.status === "pending_coach" || g?.status === "proposed")
          .map(() => "S"),
        rankingNoticePending ? "M" : null,
      ].filter((d) => DIMENSIONS.includes(d))
    ),
  ];
  const dim = DIMENSIONS.includes(activeDim)
    ? activeDim
    : pendingDims[0] || profile?.activeDimension || "S";
  const canNotifyRank = Boolean(peer?.user_id) && rankingNoticePending;
  const notifyRanking = async () => {
    if (!peer?.user_id || notifyingRank) return;
    setNotifyingRank(true);
    try {
      await acknowledgeFlttRankingNotice(peer.user_id);
      await load();
      await onReviewed?.();
    } catch (err) {
      window.alert(
        err?.response?.data?.message || err?.message || "Notify failed"
      );
    } finally {
      setNotifyingRank(false);
    }
  };
  const dimEvents = pendingEvents.filter(
    (e) => String(e.dimension || "").toUpperCase() === dim
  );
  const primaryPending =
    dimEvents[0] ||
    pendingEvents.find(
      (e) =>
        e.event_type === "exercise_personalization" || e.payload?.proposal_id
    ) ||
    null;
  const detailLines = slotDetailLines(profile, dim, t);
  const summaryText =
    profile?.summaries?.[dim] ||
    peer?.smart_summary ||
    t("digipingPlayerSmartPath");
  // Avoid repeating the dimension title as the only "summary"
  const dimTitle = `${labels[dim]?.letter || dim} · ${labels[dim]?.label || dim}`;
  const summaryClean =
    String(summaryText || "").trim() === dimTitle.trim() ? null : summaryText;

  const reviewEx = async (statusValue, eventId, extra = {}) => {
    const id = eventId || primaryPending?.event_id;
    if (!id || busy) return;
    setBusy(true);
    try {
      const metricKinds =
        extra.metricKinds ?? metricKindsByEvent[id] ?? undefined;
      await reviewSmartEvent(id, {
        status: statusValue,
        coachNote:
          statusValue === "approved"
            ? dim === "S"
              ? t("digipingSExerciseValidated")
              : t("digipingDimApproved", { dim })
            : t("digipingRejected"),
        ...(metricKinds?.length ? { metric_kinds: metricKinds } : {}),
      });
      await load();
      await onReviewed?.();
    } catch (err) {
      window.alert(
        err?.response?.data?.message || err?.message || "Review failed"
      );
    } finally {
      setBusy(false);
    }
  };

  const seasonResultLabel = resolveSignupSeasonResult(
    trailData?.seasonQuest || profile?.slots?.A?.seasonQuest,
    null,
    profile?.slots?.A || null,
    t
  );

  const askPingflo = () => {
    const name = peerLabel(peer);
    openDigitalTwin({
      home_role: "coach",
      seedVisible: false,
      seedMessage:
        t("digipingCoachEditPlayerLetterSeed", {
          name,
          dim,
          defaultValue: `Help me review and set ${name}'s SMART ${dim} letter. Call get_player_smart_profile with player_name="${name}".`,
        }),
    });
  };

  return (
    <div className="dp-people__smart-trail">
      {seasonResultLabel ? (
        <div className="dp-people__season-result">
          <span className="dp-people__season-kicker">{t("smartFinalObjectiveKicker")}</span>
          <strong className="dp-people__season-value">{seasonResultLabel}</strong>
        </div>
      ) : null}
      <CoachSmartGrid
        status={railStatus}
        labels={labels}
        activeDim={dim}
        pendingDims={pendingDims}
        onSelectDimension={onSelectDimension}
        t={t}
      />
      <div
        className={`dp-people__letter-sheet${
          pendingDims.includes(dim) ? " is-pending" : ""
        }`}
      >
        <header className="dp-people__letter-head">
          <span className="dp-people__letter-glyph">{labels[dim]?.letter || dim}</span>
          <div>
            <strong>
              {labels[dim]?.letter || dim} · {labels[dim]?.label || dim}
              {pendingDims.includes(dim) ? t("digipingToValidateSuffix") : ""}
            </strong>
            <span className={`dp-people__letter-state is-${railStatus[dim] || "empty"}`}>
              {coachDimLabel(railStatus[dim], t)}
            </span>
          </div>
        </header>
        {detailLines.length ? (
          <ul className="dp-people__dim-facts">
            {detailLines.map((line, idx) => (
              <li
                key={`${line.label || "f"}-${idx}`}
                className={line.label ? undefined : "is-value-only"}
              >
                {line.label ? (
                  <span className="dp-people__fact-label">{line.label}</span>
                ) : null}
                <span>{line.value}</span>
              </li>
            ))}
          </ul>
        ) : summaryClean && summaryClean !== t("digipingPlayerSmartPath") ? (
          <p className="dp-people__letter-copy">{summaryClean}</p>
        ) : (
          <p className="dp-people__letter-copy">{t("coachSmartPlayerLetterEmpty")}</p>
        )}
        {dim === "S" ? (
          <div
            className={`dp-people__s-exercise-card${
              pendingDims.includes("S") ? " is-pending" : ""
            }`}
          >
            <div className="dp-people__s-exercise-head">
              <strong>{t("digipingExercisesByGoal")}</strong>
            </div>
            {(goalExercises.length
              ? goalExercises
              : [
                  {
                    goalRole: "primary",
                    title: ex?.title,
                    status: ex?.status,
                    summary: ex?.summary,
                  },
                ]
            ).map((ge, idx) => {
              const role = ge.goalRole || ge.goal_role || "primary";
              const roleMark = role === "secondary" ? "·" : "★";
              const title =
                localizedDraftName(ge, lang) ||
                ge.title ||
                (role === "primary"
                  ? localizedDraftName(ex, lang) || ex?.title
                  : null) ||
                (role === String(primaryPending?.payload?.goal_role || "")
                  ? localizedDraftName(primaryPending?.payload, lang)
                  : null);
              const st = ge.status || (title ? ex?.status : null);
              return (
                <div key={`${role}-${ge.attributeId || idx}`} className="dp-people__goal-ex">
                  <div className="dp-people__goal-ex-head">
                    <span>
                      {roleMark}{" "}
                      {ge.goalLabel ||
                        (role === "primary"
                          ? t("digipingPrimaryGoal")
                          : t("digipingSecondaryGoal"))}
                    </span>
                    {st ? (
                      <span className={`dp-people__s-ex-status is-${st}`}>{st}</span>
                    ) : null}
                  </div>
                  <p className="dp-people__s-exercise-title">
                    {title || t("digipingNoExerciseForGoal")}
                  </p>
                  {ge.summary ? (
                    <p className="dp-people__s-exercise-summary">{ge.summary}</p>
                  ) : null}
                </div>
              );
            })}
            {dimEvents
              .filter(
                (e) =>
                  e.event_type === "exercise_personalization" ||
                  e.payload?.proposal_id
              )
              .map((ev) => (
                <div key={ev.event_id} className="digiping-chat__coach-review-block">
                  <p className="dp-people__s-exercise-title">
                    {t("digipingToValidatePrefix")}
                    {localizedDraftName(ev.payload, lang) ||
                      ev.title ||
                      ev.event_type}
                    {ev.payload?.goal_role ? ` (${ev.payload.goal_role})` : ""}
                  </p>
                  {ev.body ? (
                    <p className="dp-people__s-exercise-summary">{ev.body}</p>
                  ) : null}
                  {resolveExIdForMetricPicker(ev) ? (
                    <CoachObjectiveMetricPicker
                      playerUserId={peer?.user_id}
                      exId={resolveExIdForMetricPicker(ev)}
                      goalRole={ev.payload?.goal_role || "primary"}
                      goalLabel={ev.payload?.goal_label || null}
                      mode="validate"
                      onChange={(kinds) =>
                        setMetricKindsByEvent((prev) => ({
                          ...prev,
                          [ev.event_id]: kinds,
                        }))
                      }
                    />
                  ) : null}
                  <div className="digiping-chat__coach-review-actions">
                    <button
                      type="button"
                      className="digiping-chat__chip"
                      disabled={busy}
                      onClick={() => reviewEx("approved", ev.event_id)}
                    >
                      {t("digipingValidateAssign")}
                    </button>
                    <button
                      type="button"
                      className="digiping-chat__chip"
                      disabled={busy}
                      onClick={() => reviewEx("rejected", ev.event_id)}
                    >
                      {t("digipingReject")}
                    </button>
                  </div>
                </div>
              ))}
          </div>
        ) : null}
        {dim === "M" &&
        (trailData?.officialRanking ||
          canShowFlttRankingChart(trailData?.rankingHistory)) ? (
          <div className="dp-people__ranking-slip">
            <FlttRankingSlip
              ranking={
                trailData.officialRanking ||
                (() => {
                  const hist = trailData.rankingHistory || [];
                  const last = hist[hist.length - 1] || {};
                  return {
                    classement: last.classement,
                    ranking_points: last.ranking_points,
                    ranking_place: last.ranking_place,
                    week_start: last.week_start,
                  };
                })()
              }
              history={trailData.rankingHistory}
              t={t}
              showNotify={canNotifyRank}
              onNotify={notifyRanking}
              notifying={notifyingRank}
              showHistory
              chartTone="dark"
            />
          </div>
        ) : null}
        {dim === "M" && trailData?.sprintProgress?.exercises?.length ? (
          <SprintExerciseTable
            sprintProgress={trailData.sprintProgress}
            userId={peer?.user_id}
            coachView
            compact
          />
        ) : null}
        {dimEvents.length > 0 && dim !== "S" ? (
          <div className="digiping-chat__coach-review-actions">
            {dimEvents.map((ev) => (
              <button
                key={ev.event_id}
                type="button"
                className="digiping-chat__chip"
                disabled={busy}
                onClick={() => reviewEx("approved", ev.event_id)}
              >
                {t("smartLetterValidate", { letter: dim })}
              </button>
            ))}
          </div>
        ) : null}
        <div className="dp-people__letter-actions">
          <button type="button" className="digiping-chat__chip" onClick={askPingflo}>
            {t("smartLetterEdit")}
          </button>
        </div>
      </div>
    </div>
  );
}

function SmartReviewPanel({ events, lang, onReviewed, activeDim, onSelectDimension }) {
  const { t } = useTranslation();
  const [reviewingId, setReviewingId] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  if (!events?.length) return null;

  const act = async (eventId, status) => {
    if (busy) return;
    if (status === "commented" && !String(note || "").trim()) {
      window.alert(t("digipingWriteComment"));
      return;
    }
    setBusy(true);
    try {
      await reviewSmartEvent(eventId, {
        status,
        coachNote: note || "",
      });
      setNote("");
      setReviewingId(null);
      await onReviewed?.();
    } catch (err) {
      window.alert(
        err?.response?.data?.message || err?.message || "Review failed"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dp-people__smart-review">
      <div className="dp-people__smart-review-title">
        {t("digipingSmartValidationCount", { count: events.length })}
      </div>
      {events.map((ev) => {
        const evDim = String(ev.dimension || "").toUpperCase();
        const isActive = activeDim && evDim === String(activeDim).toUpperCase();
        return (
        <div
          key={ev.event_id}
          className={`dp-people__smart-event${isActive ? " is-active" : ""}`}
        >
          <button
            type="button"
            className="dp-people__smart-event-main"
            onClick={() => {
              onSelectDimension?.(evDim);
              setReviewingId((cur) =>
                cur === ev.event_id ? null : ev.event_id
              );
            }}
          >
            <span
              className={`dimension-chip dp-em dp-em--${String(
                ev.dimension || "r"
              ).toLowerCase()}`}
            >
              {ev.dimension_ref || ev.dimension}
            </span>
            <span className="dp-people__smart-event-copy">
              <strong>{ev.title}</strong>
              {ev.body ? <span>{ev.body}</span> : null}
            </span>
          </button>
          {reviewingId === ev.event_id ? (
            <div className="dp-people__smart-event-actions">
              <textarea
                className="digiping-chat__input"
                rows={2}
                placeholder={t("digipingCoachCommentPlaceholder")}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <div className="dp-people__smart-btns">
                <button
                  type="button"
                  className="digiping-chat__chip"
                  disabled={busy}
                  onClick={() => act(ev.event_id, "approved")}
                >
                  {t("digipingApprove")}
                </button>
                <button
                  type="button"
                  className="digiping-chat__chip"
                  disabled={busy}
                  onClick={() => act(ev.event_id, "commented")}
                >
                  {t("digipingComment")}
                </button>
                <button
                  type="button"
                  className="digiping-chat__chip"
                  disabled={busy}
                  onClick={() => act(ev.event_id, "rejected")}
                >
                  {t("digipingReject")}
                </button>
              </div>
            </div>
          ) : null}
        </div>
        );
      })}
    </div>
  );
}

/**
 * Digiping people chat — coach↔player DMs + group thread.
 * Used on Home Digiping tabs and Live Training Session tab.
 */
export default function DigipingPeopleChat({
  role = "player",
  userId,
  tsId = null,
  teId = null,
  grId = null,
  clubId: clubIdProp = null,
  presentPlayers = [],
  sessionCoachUserId = null,
  initialPeerId = null,
  initialThread = null, // 'group' | { peerId }
  initialSelectedGroupId = null,
  onUnreadChange = null,
  onInitialPeerConsumed = null,
}) {
  const { t, i18n } = useTranslation();
  const lang2 = (i18n.language || "en").substring(0, 2);
  const isStaffRoster = canBrowseClubChatRoster(role);
  /** SMART validation trail on player DMs — coaches/admins only. */
  const canReviewSmart = role === "coach" || role === "admin";
  const [peers, setPeers] = useState({
    players: [],
    coaches: [],
    groups: [],
    coach: null,
  });
  const [loadingPeers, setLoadingPeers] = useState(true);
  const [thread, setThread] = useState(null); // { kind:'dm', peerId, name } | { kind:'group', grId, name }
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSmartId, setExpandedSmartId] = useState(null);
  const [trailDim, setTrailDim] = useState("S");
  const [selectedGroupId, setSelectedGroupId] = useState(
    () => (initialSelectedGroupId ? String(initialSelectedGroupId) : null)
  );
  const [composerOpen, setComposerOpen] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const voiceSupported = checkBrowserCapability().ok;
  const {
    listening: voiceListening,
    onPartial: onVoicePartial,
    onTranscript: onVoiceTranscript,
    onListeningChange: onVoiceListeningChange,
    resetBuffer: resetVoiceBuffer,
    markEditing: markVoiceEditing,
  } = useLiveVoiceTranscript({
    value: text,
    setValue: setText,
    appendMode: false,
  });

  useEffect(() => {
    if (!composerOpen || !inputRef.current) return undefined;
    const tmr = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(tmr);
  }, [composerOpen]);

  const ensureSocket = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token) return null;
    return getSocket() || initializeSocket(token);
  }, []);

  const loadPeers = useCallback(async (clubIdHint) => {
    setLoadingPeers(true);
    setError(null);
    try {
      const stored = readStoredUser();
      const clubId = clubIdHint || clubIdProp || readActiveClubId(stored);
      const data = await fetchChatPeers({
        as: isStaffRoster ? role || "coach" : "player",
        grId: grId || undefined,
        clubId,
        days: 14,
      });
      setPeers(data);
      onUnreadChange?.(Number(data.unread_total) || 0);
    } catch (err) {
      console.warn("Digiping peers failed:", err?.message || err);
      setError(err?.response?.data?.message || err.message || "Failed to load");
    } finally {
      setLoadingPeers(false);
    }
  }, [isStaffRoster, role, grId, clubIdProp, onUnreadChange]);

  useEffect(() => {
    loadPeers();
  }, [loadPeers]);

  // Refresh connected status while the People list is open.
  useEffect(() => {
    if (thread) return undefined;
    const timer = window.setInterval(() => {
      loadPeers();
    }, 20000);
    return () => window.clearInterval(timer);
  }, [thread, loadPeers]);

  useEffect(() => {
    const onClubChanged = (event) => {
      const nextClubId =
        event?.detail?.club_id || readActiveClubId(readStoredUser());
      loadPeers(nextClubId);
    };
    window.addEventListener("smarttt-club-changed", onClubChanged);
    window.addEventListener("smarttt-user-updated", onClubChanged);
    return () => {
      window.removeEventListener("smarttt-club-changed", onClubChanged);
      window.removeEventListener("smarttt-user-updated", onClubChanged);
    };
  }, [loadPeers]);

  // Live Training: enrich coach player list with present players first
  const playerList = useMemo(() => {
    const base = [...(peers.players || [])];
    if (!presentPlayers?.length) return base;
    const activeClubId =
      clubIdProp || readActiveClubId(readStoredUser()) || peers.club_id;
    const byId = Object.fromEntries(base.map((p) => [String(p.user_id), p]));
    const presentIds = new Set(
      presentPlayers.map((p) => String(p.user_id)).filter(Boolean)
    );
    for (const p of presentPlayers) {
      const id = String(p.user_id);
      if (!id) continue;
      if (
        activeClubId &&
        p.club_id &&
        String(p.club_id) !== String(activeClubId)
      ) {
        continue;
      }
      if (!byId[id]) {
        base.unshift({
          user_id: p.user_id,
          name:
            p.user_alias ||
            [p.user_first_name, p.user_last_name].filter(Boolean).join(" ") ||
            String(p.user_id),
          user_image: p.user_image || null,
          sessions_present: 0,
          present: true,
          unread: 0,
          smart_exists: false,
          pending_validations: 0,
          pending_events: [],
        });
      } else {
        byId[id].present = true;
      }
    }
    // Connected first, then pending SMART, live session presence, sessions / SMART / name
    return base.sort((a, b) => {
      if (Number(b.connected) !== Number(a.connected)) {
        return Number(b.connected) - Number(a.connected);
      }
      if ((b.pending_validations || 0) !== (a.pending_validations || 0)) {
        return (b.pending_validations || 0) - (a.pending_validations || 0);
      }
      const ap = presentIds.has(String(a.user_id)) ? 1 : 0;
      const bp = presentIds.has(String(b.user_id)) ? 1 : 0;
      if (bp !== ap) return bp - ap;
      if ((b.sessions_present || 0) !== (a.sessions_present || 0)) {
        return (b.sessions_present || 0) - (a.sessions_present || 0);
      }
      if (Number(b.smart_exists) !== Number(a.smart_exists)) {
        return Number(b.smart_exists) - Number(a.smart_exists);
      }
      return String(a.name || "").localeCompare(String(b.name || ""));
    }).filter((p) => !userId || String(p.user_id) !== String(userId));
  }, [peers.players, presentPlayers, userId, clubIdProp]);

  const coachList = useMemo(() => {
    const list = [...(peers.coaches || [])];
    return list
      .filter((c) => !userId || String(c.user_id) !== String(userId))
      .sort((a, b) => {
        if (Number(b.connected) !== Number(a.connected)) {
          return Number(b.connected) - Number(a.connected);
        }
        return String(a.name || "").localeCompare(String(b.name || ""));
      });
  }, [peers.coaches, userId]);

  const visiblePlayers = useMemo(() => {
    if (!selectedGroupId) return playerList;
    return playerList.filter((p) =>
      (p.gr_ids || []).map(String).includes(String(selectedGroupId))
    );
  }, [playerList, selectedGroupId]);

  const pendingTotal = useMemo(
    () =>
      (peers.players || []).reduce(
        (n, p) => n + (Number(p.pending_validations) || 0),
        0
      ),
    [peers.players]
  );

  const threadPeer = useMemo(() => {
    if (!thread || thread.kind !== "dm") return null;
    return playerList.find((p) => String(p.user_id) === String(thread.peerId));
  }, [thread, playerList]);

  useEffect(() => {
    setComposerOpen(false);
    const firstPending = threadPeer?.pending_events?.[0]?.dimension;
    setTrailDim(
      firstPending ? String(firstPending).toUpperCase() : "S"
    );
  }, [thread?.peerId, thread?.kind, threadPeer?.user_id]);

  const groupOptions = useMemo(() => {
    const groups = peers.groups || [];
    if (grId) {
      const hit = groups.find((g) => String(g.gr_id) === String(grId));
      return hit ? [hit] : [];
    }
    return groups;
  }, [peers.groups, grId]);

  useEffect(() => {
    if (!initialSelectedGroupId) return;
    setSelectedGroupId(String(initialSelectedGroupId));
  }, [initialSelectedGroupId]);

  const openDm = useCallback((peerId, name) => {
    if (!peerId || (userId && String(peerId) === String(userId))) return;
    setThread({ kind: "dm", peerId: String(peerId), name: name || "Chat" });
  }, [userId]);

  const openGroup = useCallback((g) => {
    if (!g?.gr_id) return;
    setThread({
      kind: "group",
      grId: String(g.gr_id),
      name: g.gr_name || t("group") || "Group",
    });
  }, [t]);

  const handleGroupTap = useCallback((g) => {
    if (!g?.gr_id) return;
    const id = String(g.gr_id);
    if (String(selectedGroupId) === id) {
      openGroup(g);
      return;
    }
    setSelectedGroupId(id);
  }, [openGroup, selectedGroupId]);

  // Auto-open from props
  useEffect(() => {
    if (thread) return;
    if (initialThread === "group" && (grId || groupOptions[0])) {
      const scopedGroup = grId
        ? groupOptions.find((g) => String(g.gr_id) === String(grId))
        : groupOptions[0];
      if (scopedGroup) {
        openGroup(scopedGroup);
        return;
      }
    }
    if (initialPeerId) {
      const p = playerList.find((x) => String(x.user_id) === String(initialPeerId));
      openDm(initialPeerId, p ? peerLabel(p) : null);
      onInitialPeerConsumed?.();
      return;
    }
    if (!isStaffRoster && peers.coach?.user_id) {
      // Stay on list until user taps Coach — keep list with single coach row
    }
    if (
      !isStaffRoster &&
      sessionCoachUserId &&
      String(sessionCoachUserId) !== String(userId || "") &&
      !peers.coach
    ) {
      openDm(sessionCoachUserId, t("coach") || "Coach");
    }
  }, [
    thread,
    initialThread,
    initialPeerId,
    grId,
    groupOptions,
    openGroup,
    openDm,
    playerList,
    isStaffRoster,
    peers.coach,
    sessionCoachUserId,
    userId,
    t,
  ]);

  const loadHistory = useCallback(async () => {
    if (!thread) {
      setMessages([]);
      return;
    }
      try {
        const rows = await fetchChatHistory({
          threadKind: thread.kind,
          peerId: thread.peerId,
          grId: thread.grId,
        });
        setMessages(Array.isArray(rows) ? rows : []);
      } catch (err) {
        console.warn("Digiping history failed:", err?.message || err);
      }
  }, [thread]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, thread]);

  // Realtime
  useEffect(() => {
    const sock = ensureSocket();
    if (!sock || !userId) return undefined;

    const onNew = (msg) => {
      if (!msg) return;
      setMessages((prev) => {
        if (!thread) return prev;
        if (thread.kind === "group") {
          if (String(msg.gr_id) !== String(thread.grId) || msg.thread_kind !== "group") {
            return prev;
          }
        } else {
          const sid = String(msg.sender_id);
          const rid = String(msg.receiver_id || "");
          const peer = String(thread.peerId);
          const me = String(userId);
          const involves =
            (sid === peer && rid === me) || (sid === me && rid === peer);
          if (!involves || msg.thread_kind === "group") return prev;
        }
        if (prev.some((m) => m.message_id === msg.message_id)) return prev;
        return [...prev, msg];
      });
      if (!thread) {
        loadPeers();
      }
    };

    sock.on("digipingChatNewMessage", onNew);
    return () => {
      sock.off("digipingChatNewMessage", onNew);
    };
  }, [ensureSocket, userId, thread, loadPeers]);

  const send = async () => {
    const trimmed = text.trim();
    if (!trimmed || !thread || sending) return;
    setSending(true);
    setError(null);
    const optimistic = {
      message_id: `local_${Date.now()}`,
      sender_id: userId,
      receiver_id: thread.kind === "dm" ? thread.peerId : null,
      thread_kind: thread.kind === "group" ? "group" : "dm",
      gr_id: thread.kind === "group" ? thread.grId : null,
      message_type: "text",
      text_message: trimmed,
      createdAt: new Date().toISOString(),
      sender: { user_id: userId },
    };
    setMessages((prev) => [...prev, optimistic]);
    resetVoiceBuffer();
    try {
      const body = {
        thread_kind: thread.kind === "group" ? "group" : "dm",
        message_type: "text",
        text_message: trimmed,
        ts_id: tsId || undefined,
        te_id: teId || undefined,
      };
      if (thread.kind === "group") {
        body.gr_id = thread.grId;
      } else {
        body.receiver_id = thread.peerId;
      }
      const saved = await sendChatMessage(body);
      if (saved?.message_id) {
        setMessages((prev) => {
          const withoutLocal = prev.filter(
            (m) => m.message_id !== optimistic.message_id
          );
          if (withoutLocal.some((m) => m.message_id === saved.message_id)) {
            return withoutLocal;
          }
          return [...withoutLocal, saved];
        });
      }
      setComposerOpen(false);
    } catch (err) {
      console.error("Digiping send failed:", err);
      setMessages((prev) =>
        prev.filter((m) => m.message_id !== optimistic.message_id)
      );
      setText(trimmed);
      setComposerOpen(true);
      setError(err?.response?.data?.message || err.message || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!userId) {
    return (
      <div className="dp-people">
        <p className="dp-people__empty">{t("loginRequired") || "Sign in to chat"}</p>
      </div>
    );
  }

  // Thread view
  if (thread) {
    return (
      <div className={`dp-people dp-people--thread${canReviewSmart && thread.kind === "dm" ? " dp-people--player-smart" : ""}`}>
        <div className="dp-people__thread-bar">
          <button
            type="button"
            className="digiping-chat__icon-btn"
            onClick={() => setThread(null)}
            aria-label={t("back") || "Back"}
          >
            <ArrowBackIcon fontSize="small" />
          </button>
          <div className="dp-people__thread-title">
            <span className="digiping-chat__label">
              {thread.kind === "group"
                ? t("group") || "Group"
                : isStaffRoster
                  ? t("player") || "Player"
                  : t("coach") || "Coach"}
            </span>
            <span className="dp-people__thread-name">
              {thread.name}
              {thread.kind === "dm" &&
              (threadPeer?.connected ||
                (peers.coach &&
                  String(peers.coach.user_id) === String(thread.peerId) &&
                  peers.coach.connected)) ? (
                <ConnectedBadge connected t={t} />
              ) : null}
            </span>
          </div>
          <button
            type="button"
            className="dp-people__thread-msg"
            onClick={() => {
              setComposerOpen(true);
              window.setTimeout(() => inputRef.current?.focus(), 50);
            }}
          >
            {t("coachSmartMessagePlayer")}
          </button>
        </div>
        {error ? <p className="dp-people__error">{error}</p> : null}

        {canReviewSmart && thread.kind === "dm" && threadPeer ? (
          <div className="dp-people__smart-scroll">
            {threadPeer.pending_events?.length ? (
              <SmartReviewPanel
                events={threadPeer.pending_events}
                lang={lang2}
                onReviewed={loadPeers}
                activeDim={trailDim}
                onSelectDimension={setTrailDim}
              />
            ) : null}
            <PlayerSmartTrail
              peer={threadPeer}
              lang={lang2}
              onReviewed={loadPeers}
              activeDim={trailDim}
              onSelectDimension={setTrailDim}
            />
          </div>
        ) : null}

        <div className={`digiping-chat__history dp-people__history${messages.length === 0 ? " is-empty" : ""}`}>
          {messages.length === 0 ? (
            <div ref={endRef} />
          ) : (
            messages.map((m) => {
              const sid = messageSenderId(m);
              const mine = sid != null && String(sid) === String(userId);
              const name =
                m.sender?.user_alias ||
                m.sender?.user_first_name ||
                (mine ? null : thread.name);
              return (
                <div
                  key={m.message_id}
                  className={`dp-people__msg ${
                    mine ? "dp-people__msg--mine" : "dp-people__msg--theirs"
                  }`}
                >
                  {!mine ? (
                    <div className="digiping-chat__avatar digiping-chat__avatar--sm">
                      {m.sender?.user_image ? (
                        <img src={m.sender.user_image} alt="" />
                      ) : (
                        <span>{(name || "?").charAt(0).toUpperCase()}</span>
                      )}
                    </div>
                  ) : null}
                  <div className="dp-people__bubble">
                    {!mine && name ? (
                      <div className="digiping-chat__coach-label-row">
                        <span className="digiping-chat__label">{name}</span>
                      </div>
                    ) : null}
                    <div className="dp-people__text">
                      {m.message_type === "voice" && m.voice_message_url
                        ? `🎤 ${t("voiceMessage") || "Voice message"}`
                        : m.message_type === "video" && m.video_message_url
                          ? `🎬 ${t("videoMessage") || "Video message"}`
                          : m.message_type === "team_update" ||
                              m.message_type === "system"
                            ? (
                              <em className="dp-people__system-msg">
                                {m.text_message || ""}
                              </em>
                            )
                          : m.text_message || ""}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={endRef} />
        </div>

        <footer className="digiping-chat__footer">
          {composerOpen ? (
            <div className="digiping-chat__composer">
              <input
                ref={inputRef}
                type="text"
                className="digiping-chat__input dp-people__input"
                placeholder={
                  voiceListening
                    ? t("cmDictateListening", "Listening…")
                    : t("digipingChatPlaceholder") || "Message…"
                }
                value={text}
                onChange={(e) => {
                  markVoiceEditing();
                  setText(e.target.value);
                }}
                onKeyDown={handleKeyDown}
                disabled={sending}
                readOnly={voiceListening}
              />
              {voiceSupported ? (
                <div className="digiping-chat__voice">
                  <CoachVoiceButton
                    disabled={sending}
                    languageHint={lang2}
                    onPartial={onVoicePartial}
                    onListeningChange={onVoiceListeningChange}
                    onTranscript={onVoiceTranscript}
                  />
                </div>
              ) : null}
              <button
                type="button"
                className="digiping-chat__send"
                onClick={send}
                disabled={!text.trim() || sending || voiceListening}
                aria-label={t("send") || "Send"}
              >
                <SendIcon fontSize="small" />
              </button>
            </div>
          ) : (
            <div className="digiping-chat__composer digiping-chat__composer--idle">
              <button
                type="button"
                className="digiping-chat__composer-idle"
                onClick={() => {
                  setComposerOpen(true);
                  window.setTimeout(() => inputRef.current?.focus(), 50);
                }}
              >
                {t("digipingChatPlaceholder") || "Message…"}
              </button>
            </div>
          )}
        </footer>
      </div>
    );
  }

  // Peer list
  return (
    <div className="dp-people">
      {error ? <p className="dp-people__error">{error}</p> : null}
      {loadingPeers ? (
        <p className="dp-people__empty">{t("loading") || "Loading…"}</p>
      ) : (
        <div className="dp-people__list">
          {groupOptions.length > 0 ? (
            <div className="dp-people__section">
              <div className="dp-people__section-title">
                {t("group") || "Group"}
              </div>
              {isStaffRoster ? (
                <>
                  <p className="dp-people__section-lead">{t("coachSmartTapGroupHint")}</p>
                  {groupOptions.map((g) => {
                    const selected = String(selectedGroupId) === String(g.gr_id);
                    return (
                      <div
                        key={g.gr_id}
                        className={`dp-people__group-block${selected ? " is-selected" : ""}`}
                      >
                        <button
                          type="button"
                          className={`dp-people__row${selected ? " is-selected" : ""}`}
                          onClick={() => handleGroupTap(g)}
                        >
                          <div className="digiping-chat__avatar digiping-chat__avatar--sm">
                            <DigipingMark size={20} />
                          </div>
                          <div className="dp-people__row-text">
                            <span className="dp-people__row-name">
                              {g.gr_name || g.gr_id}
                            </span>
                            <span className="dp-people__row-meta">
                              {selected
                                ? t("coachSmartTapAgainToMessage")
                                : t("coachSmartSelectGroup")}
                            </span>
                          </div>
                        </button>
                        {selected ? (
                          <button
                            type="button"
                            className="dp-people__group-message"
                            onClick={() => openGroup(g)}
                          >
                            {t("coachSmartMessageGroup")}
                          </button>
                        ) : null}
                      </div>
                    );
                  })}
                </>
              ) : (
                groupOptions.map((g) => (
                  <button
                    key={g.gr_id}
                    type="button"
                    className="dp-people__row"
                    onClick={() => openGroup(g)}
                  >
                    <div className="digiping-chat__avatar digiping-chat__avatar--sm">
                      <DigipingMark size={20} />
                    </div>
                    <div className="dp-people__row-text">
                      <span className="dp-people__row-name">
                        {g.gr_name || g.gr_id}
                      </span>
                      <span className="dp-people__row-meta">
                        {t("digipingChatGroupHint") || "Everyone in the group"}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          ) : null}

          {!isStaffRoster ? (
            <div className="dp-people__section">
              <div className="dp-people__section-title">
                {t("coach") || "Coach"}
              </div>
              {(peers.coach?.user_id && String(peers.coach.user_id) !== String(userId || "")) ||
              (sessionCoachUserId && String(sessionCoachUserId) !== String(userId || "")) ? (
                <button
                  type="button"
                  className="dp-people__row"
                  onClick={() =>
                    openDm(
                      peers.coach?.user_id &&
                        String(peers.coach.user_id) !== String(userId || "")
                        ? peers.coach.user_id
                        : sessionCoachUserId,
                      peers.coach?.name || t("coach") || "Coach"
                    )
                  }
                >
                  <div className="digiping-chat__avatar digiping-chat__avatar--sm">
                    {peers.coach?.user_image ? (
                      <img src={peers.coach.user_image} alt="" />
                    ) : (
                      <span>C</span>
                    )}
                  </div>
                  <div className="dp-people__row-text">
                    <span className="dp-people__row-name">
                      {peers.coach?.name || t("coach") || "Coach"}
                      <ConnectedBadge connected={peers.coach?.connected} t={t} />
                    </span>
                    <span className="dp-people__row-meta">
                      {peers.coach?.connected
                        ? t("digipingChatConnectedHint")
                        : t("digipingChatCoachHint") || "Direct message"}
                    </span>
                  </div>
                </button>
              ) : (
                <p className="dp-people__empty">
                  {t("digipingChatNoCoach") ||
                    "No coach assigned to your group yet."}
                </p>
              )}
            </div>
          ) : (
            <>
            {coachList.length > 0 ? (
              <div className="dp-people__section">
                <div className="dp-people__section-title">
                  {t("coaches") || t("coach") || "Coaches"}
                </div>
                <p className="dp-people__section-lead">
                  {t("digipingChatTapCoachHint") ||
                    "Tap a coach to message them by name."}
                </p>
                {coachList.map((c) => (
                  <button
                    key={c.user_id}
                    type="button"
                    className={`dp-people__row${c.connected ? " is-connected" : ""}`}
                    onClick={() => openDm(c.user_id, c.name || t("coach") || "Coach")}
                  >
                    <div className="digiping-chat__avatar digiping-chat__avatar--sm">
                      {c.user_image ? (
                        <img src={c.user_image} alt="" />
                      ) : (
                        <span>{String(c.name || "C").charAt(0).toUpperCase()}</span>
                      )}
                    </div>
                    <div className="dp-people__row-text">
                      <span className="dp-people__row-name">
                        {c.name || t("coach") || "Coach"}
                        <ConnectedBadge connected={c.connected} t={t} />
                      </span>
                      <span className="dp-people__row-meta">
                        {c.connected
                          ? t("digipingChatConnectedHint")
                          : t("digipingChatCoachHint") || "Direct message"}
                      </span>
                    </div>
                    {c.unread > 0 ? (
                      <span className="dp-people__badge">
                        {c.unread > 9 ? "9+" : c.unread}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="dp-people__section">
              <div className="dp-people__section-title">
                {t("players") || "Players"}
                <span className="dp-people__section-hint">
                  {selectedGroupId
                    ? t("coachSmartPlayersInGroup", {
                        group:
                          groupOptions.find(
                            (g) => String(g.gr_id) === String(selectedGroupId)
                          )?.gr_name || "",
                      })
                    : t("digipingSmartFirstPresence", {
                        days: peers.presence_days || 14,
                      })}
                  {pendingTotal > 0
                    ? t("digipingToReviewCount", { count: pendingTotal })
                    : ""}
                </span>
              </div>
              <p className="dp-people__section-lead">{t("coachSmartTapPlayerHint")}</p>
              {visiblePlayers.length === 0 ? (
                <p className="dp-people__empty">
                  {t("digipingChatNoPlayers") || "No players in your groups."}
                </p>
              ) : (
                visiblePlayers.map((p) => {
                  const expanded =
                    String(expandedSmartId) === String(p.user_id);
                  return (
                    <div key={p.user_id} className="dp-people__player-block">
                      <button
                        type="button"
                        className={`dp-people__row${
                          p.smart_exists ? " dp-people__row--smart" : ""
                        }${
                          (p.pending_validations || 0) > 0
                            ? " dp-people__row--pending"
                            : ""
                        }${p.connected ? " is-connected" : ""}`}
                        onClick={() => openDm(p.user_id, peerLabel(p))}
                      >
                        <div className="digiping-chat__avatar digiping-chat__avatar--sm">
                          {p.user_image ? (
                            <img src={p.user_image} alt="" />
                          ) : (
                            <span>
                              {peerLabel(p).charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="dp-people__row-text">
                          <span className="dp-people__row-name">
                            {peerLabel(p)}
                            <ConnectedBadge connected={p.connected} t={t} />
                            {p.smart_exists ? (
                              <span className="dp-people__smart-tag">SMART</span>
                            ) : null}
                            {p.present ? (
                              <span className="dp-people__present">
                                {t("present") || "present"}
                              </span>
                            ) : null}
                          </span>
                          <span className="dp-people__row-meta">
                            {p.smart_summary
                              ? p.smart_summary
                              : t("digipingNoSmartProfile")}
                          </span>
                          <span className="dp-people__row-meta dp-people__row-meta--presence">
                            {(p.sessions_present || 0) > 0
                              ? `${p.sessions_present} ${t("digipingSessionsPresence")}`
                              : t("digipingNoPresenceWindow")}
                          </span>
                          {p.letters?.length ? (
                            <div className="dp-people__smart-letters">
                              {p.letters.map((L) => (
                                <span
                                  key={L.letter}
                                  className={`digiping-chat__smart-letter digiping-chat__smart-letter--${
                                    L.status || "empty"
                                  }`}
                                  title={`${L.letter}: ${L.status}`}
                                >
                                  {L.letter}
                                </span>
                              ))}
                            </div>
                          ) : null}
                        </div>
                        <div className="dp-people__row-badges">
                          {(p.pending_validations || 0) > 0 ? (
                            <button
                              type="button"
                              className="dp-people__badge dp-people__badge--smart"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedSmartId((cur) =>
                                  String(cur) === String(p.user_id)
                                    ? null
                                    : p.user_id
                                );
                              }}
                            >
                              R {p.pending_validations}
                            </button>
                          ) : null}
                          {p.unread > 0 ? (
                            <span className="dp-people__badge">
                              {p.unread > 9 ? "9+" : p.unread}
                            </span>
                          ) : null}
                        </div>
                      </button>
                      {expanded && p.pending_events?.length ? (
                        <SmartReviewPanel
                          events={p.pending_events}
                          lang={lang2}
                          onReviewed={async () => {
                            await loadPeers();
                            setExpandedSmartId(null);
                          }}
                        />
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
